/**
 * useEditorialReveal — IntersectionObserver-based scroll reveal that powers
 * `data-reveal="..."` markup.  Backward-compatible with the existing
 * `data-reveal="fade-up"` CSS in index.css; adds support for the new
 * variants defined in styles/design-system.css:
 *
 *   - "rise"           — 40px upward translation with long ease
 *   - "mask-up"        — clip-path inset reveal, like a magazine page lifting
 *   - "gold-underline" — gold hairline draws under the element
 *   - "letter-stagger" — children spans staggered up one-by-one
 *
 * Usage (mark elements with data-reveal="variant" or call the hook from a
 * page component):
 *
 *   useEditorialReveal();         // observes every [data-reveal] on the page
 *   useEditorialReveal({ rootMargin: "-10% 0px" });
 *
 *   <h1 data-reveal="mask-up">A studio worth arriving to.</h1>
 */
import { useEffect } from "react";

export default function useEditorialReveal(options = {}) {
  const { rootMargin = "0px 0px -8% 0px", threshold = 0.12, once = true } = options;

  useEffect(() => {
    if (typeof window === "undefined") return;

    const prefersReduced =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in-view");
            if (once) io.unobserve(entry.target);
          } else if (!once) {
            entry.target.classList.remove("in-view");
          }
        });
      },
      { rootMargin, threshold }
    );

    const observed = new WeakSet();
    const observe = (node) => {
      if (!(node instanceof Element) || observed.has(node)) return;
      observed.add(node);
      if (prefersReduced) {
        node.classList.add("in-view");
        return;
      }
      io.observe(node);
    };

    // Initial sweep
    document.querySelectorAll("[data-reveal]").forEach(observe);

    // Watch for [data-reveal] nodes added later (e.g., after async API loads)
    const mo = new MutationObserver((mutations) => {
      mutations.forEach((m) => {
        m.addedNodes.forEach((n) => {
          if (!(n instanceof Element)) return;
          if (n.hasAttribute && n.hasAttribute("data-reveal")) observe(n);
          if (n.querySelectorAll) n.querySelectorAll("[data-reveal]").forEach(observe);
        });
      });
    });
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      io.disconnect();
      mo.disconnect();
    };
  }, [rootMargin, threshold, once]);
}
